/**
 * useEmailHygieneReconciliationActiveCount
 *
 * DASH-EMAIL-013: honest active-reconciliation count for the
 * Deliverability & Hygiene "Reconciliation Required" tile.
 *
 * There is no dedicated count endpoint — RECON-002's list API already
 * returns an authoritative `total` cheaply via `limit=1` (the smallest
 * page that still returns a real backend-computed total; `items` itself
 * is discarded). This is real backend data, never a frontend-computed
 * length or a fabricated number: on failure this hook reports `error`,
 * and callers must render an unavailable state rather than falling back
 * to 0 (see EmailDeliverabilityView.jsx).
 *
 * EMAIL-HYGIENE-GATE-3A: `total` is now the COMBINED count (native
 * reconciliation-required recipients + cross-environment propagation
 * records whose aggregate_status is NEEDS_ATTENTION) — the same
 * server-side-computed total the queue itself uses, never a frontend
 * sum. `nativeTotal`/`propagationTotal` are also exposed (also from the
 * backend's own `native_total`/`propagation_total` fields) so a caller
 * can show the per-class breakdown without a second request.
 */
import { useState, useEffect, useCallback } from "react";
import {
  listEmailHygieneReconciliations,
  extractApiErrorMessage,
} from "../lib/emailHygieneReconciliationApi";

export function useEmailHygieneReconciliationActiveCount({ environment = "all" } = {}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(null);
  const [nativeTotal, setNativeTotal] = useState(null);
  const [propagationTotal, setPropagationTotal] = useState(null);

  const fetchCount = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listEmailHygieneReconciliations({ environment, limit: 1, offset: 0 });
      setTotal(result?.total ?? 0);
      setNativeTotal(result?.native_total ?? null);
      setPropagationTotal(result?.propagation_total ?? null);
    } catch (err) {
      setError(extractApiErrorMessage(err, "Failed to load reconciliation count"));
    } finally {
      setLoading(false);
    }
  }, [environment]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  return { loading, error, total, nativeTotal, propagationTotal, refresh: fetchCount };
}

export default useEmailHygieneReconciliationActiveCount;
